import { useState, useEffect, useCallback } from 'react';
import * as authApi from '@/api/auth';
import type { UserInfo } from '@/api/auth';

interface AuthState {
  user: UserInfo | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('access_token'),
    isLoading: true,
    isAuthenticated: false,
  });

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setState({ user: null, token: null, isLoading: false, isAuthenticated: false });
      return;
    }

    // Decode the JWT payload to get user info (no crypto verification needed)
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const roles: string[] = payload.roles || [];
      // Back-compat: if is_platform_admin is set, ensure 'admin' role is present
      if (payload.is_platform_admin && !roles.includes('admin')) {
        roles.push('admin');
      }
      const user: UserInfo = {
        id: payload.sub || payload.user_id || '',
        email: payload.email || '',
        display_name: payload.display_name || payload.name || '',
        is_platform_admin: !!payload.is_platform_admin,
        roles,
      };
      setState({ user, token, isLoading: false, isAuthenticated: true });
    } catch {
      // Token is malformed, try fetching user from API
      authApi.getMe()
        .then(user => setState({ user, token, isLoading: false, isAuthenticated: true }))
        .catch(() => {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setState({ user: null, token: null, isLoading: false, isAuthenticated: false });
        });
    }
  }, []);

  /** Store a token pair issued out-of-band (OTP login, phone registration,
   *  OAuth) and mark the session authenticated. */
  const applyAuth = useCallback((response: authApi.AuthResponse) => {
    localStorage.setItem('access_token', response.access_token);
    localStorage.setItem('refresh_token', response.refresh_token);
    setState({
      user: response.user,
      token: response.access_token,
      isLoading: false,
      isAuthenticated: true,
    });
    return response;
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    return applyAuth(response);
  }, [applyAuth]);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try { await authApi.logout(refreshToken); } catch { /* ignore */ }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/assess/login';
  }, []);

  return { ...state, login, logout, applyAuth };
}

/** Get the default redirect path for a given set of roles.
 *  Returns router-relative paths (no /assess prefix) since BrowserRouter basename="/assess"
 *  handles prefix resolution internally. */
export function getDefaultRedirect(roles: string[]): string {
  if (roles.includes('admin')) return '/admin';
  if (roles.includes('academy_admin')) return '/academy-admin';
  if (roles.includes('organizer')) return '/organizer';
  if (roles.includes('grader')) return '/grader';
  if (roles.includes('support_lead')) return '/support-lead';
  if (roles.includes('support_engineer')) return '/support-engineer';
  if (roles.includes('student')) return '/student';
  return '/login';
}
